import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@material-ui/core';
import { ArrowBack, ExpandMore } from '@material-ui/icons';
import { LifeCheckRecord, LifeCheckElement } from '../../../constants/Records';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
    form: {
      padding: theme.spacing(3),
    },
    field: {
      marginBottom: theme.spacing(2),
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    timeFields: {
      display: 'flex',
      gap: theme.spacing(2),
    },
    sectionTitle: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
    itemContainer: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
    },
    itemLabel: {
      fontWeight: 'bold',
      marginBottom: theme.spacing(1),
    },
    levelChips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    levelChip: {
      cursor: 'pointer',
    },
    levelChipSelected: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    problemCheckbox: {
      marginTop: theme.spacing(1),
      '& .MuiCheckbox-colorSecondary.Mui-checked': {
        color: theme.palette.error.main,
      },
      '& .MuiFormControlLabel-label': {
        fontWeight: 500,
      },
    },
    problemChecked: {
      backgroundColor: theme.palette.error.light + '20',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.5, 1),
      marginLeft: -theme.spacing(1),
    },
    envField: {
      marginTop: theme.spacing(1),
    },
    accordion: {
      marginBottom: theme.spacing(2),
    },
    accordionSummary: {
      backgroundColor: theme.palette.grey[100],
    },
    detailField: {
      marginTop: theme.spacing(2),
    },
    validationError: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(2),
    },
  }),
);

// ADL項目定義
const ADL_ITEMS = [
  { key: 'adl_feeding', label: '食事', levels: ['自立(10)', '一部介助(5)', '全介助(0)'], hasEnv: true },
  {
    key: 'adl_transfers',
    label: '椅子とベッド間の移乗',
    levels: ['自立(15)', '監視下(10)', '座れるが移れない(5)', '全介助(0)'],
    hasEnv: true,
  },
  { key: 'adl_grooming', label: '整容', levels: ['自立(5)', '一部介助(0)', '全介助(0)'], hasEnv: true },
  { key: 'adl_toilet_use', label: 'トイレ動作', levels: ['自立(10)', '一部介助(5)', '全介助(0)'], hasEnv: true },
  { key: 'adl_bathing', label: '入浴', levels: ['自立(5)', '一部介助(0)', '全介助(0)'], hasEnv: true },
  {
    key: 'adl_mobility',
    label: '平地歩行',
    levels: ['自立(15)', '歩行器等(10)', '車椅子操作可(5)', '全介助(0)'],
    hasEnv: true,
  },
  { key: 'adl_stairs', label: '階段昇降', levels: ['自立(10)', '一部介助(5)', '全介助(0)'], hasEnv: true },
  { key: 'adl_dressing', label: '更衣', levels: ['自立(10)', '一部介助(5)', '全介助(0)'], hasEnv: true },
  { key: 'adl_bowels', label: '排便コントロール', levels: ['自立(10)', '一部介助(5)', '全介助(0)'], hasEnv: true },
  { key: 'adl_bladder', label: '排尿コントロール', levels: ['自立(10)', '一部介助(5)', '全介助(0)'], hasEnv: true },
];

// IADL項目定義
const IADL_ITEMS = [
  { key: 'iadl_cooking', label: '調理', levels: ['自立', '見守り', '一部介助', '全介助'], hasEnv: true },
  { key: 'iadl_cleaning', label: '掃除', levels: ['自立', '見守り', '一部介助', '全介助'], hasEnv: true },
  { key: 'iadl_washing', label: '洗濯', levels: ['自立', '見守り', '一部介助', '全介助'], hasEnv: true },
];

// 居宅内動作項目定義
const BEHAVIOR_ITEMS = [
  { key: 'behavior_turn_over', label: '寝返り', levels: ['自立', '見守り', '一部介助', '全介助'], hasEnv: false },
  { key: 'behavior_raise_up', label: '起き上がり', levels: ['自立', '見守り', '一部介助', '全介助'], hasEnv: false },
  { key: 'behavior_sitting', label: '座位', levels: ['自立', '見守り', '一部介助', '全介助'], hasEnv: false },
  { key: 'behavior_stand_up', label: '立ち上がり', levels: ['自立', '見守り', '一部介助', '全介助'], hasEnv: false },
  { key: 'behavior_upright', label: '立位', levels: ['自立', '見守り', '一部介助', '全介助'], hasEnv: false },
];

// デフォルト要素
const defaultElement = (): LifeCheckElement => ({
  level: [0],
  is_problem: false,
  env: '',
});

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  currentRecord: LifeCheckRecord | null;
  isEdit: boolean;
  executing: boolean;
  created: boolean;
  updated: boolean;
  onFetchRecord: (uniqueId: string, sequence: number) => void;
  onCreate: (uniqueId: string, data: Partial<LifeCheckRecord>) => void;
  onUpdate: (uniqueId: string, sequence: number, data: Partial<LifeCheckRecord>) => void;
  onResetFlags: () => void;
}

const LifeCheckRecordForm: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  currentRecord,
  isEdit,
  executing,
  created,
  updated,
  onCreate,
  onUpdate,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  // 訪問情報
  const [staff, setStaff] = useState('');
  const [profession, setProfession] = useState('');
  const [visitedTimeStart, setVisitedTimeStart] = useState('');
  const [visitedTimeEnd, setVisitedTimeEnd] = useState('');
  const [interviewee, setInterviewee] = useState('');

  // ADL項目
  const [adlData, setAdlData] = useState<Record<string, LifeCheckElement>>(() => {
    const initial: Record<string, LifeCheckElement> = {};
    ADL_ITEMS.forEach(item => {
      initial[item.key] = defaultElement();
    });
    return initial;
  });
  const [adlDetail, setAdlDetail] = useState('');

  // IADL項目
  const [iadlData, setIadlData] = useState<Record<string, LifeCheckElement>>(() => {
    const initial: Record<string, LifeCheckElement> = {};
    IADL_ITEMS.forEach(item => {
      initial[item.key] = defaultElement();
    });
    return initial;
  });
  const [iadlDetail, setIadlDetail] = useState('');

  // 居宅内動作
  const [behaviorData, setBehaviorData] = useState<Record<string, LifeCheckElement>>(() => {
    const initial: Record<string, LifeCheckElement> = {};
    BEHAVIOR_ITEMS.forEach(item => {
      initial[item.key] = defaultElement();
    });
    return initial;
  });
  const [behaviorDetail, setBehaviorDetail] = useState('');

  // アコーディオン展開状態
  const [expandedSections, setExpandedSections] = useState({
    visit: true,
    adl: true,
    iadl: true,
    behavior: true,
  });

  // 編集時のデータ読み込み
  useEffect(() => {
    if (currentRecord && isEdit) {
      // 訪問情報
      setStaff(currentRecord.staff || '');
      setProfession(currentRecord.profession || '');
      setVisitedTimeStart(formatDatetimeForInput(currentRecord.visited_time_start));
      setVisitedTimeEnd(formatDatetimeForInput(currentRecord.visited_time_end));
      setInterviewee(currentRecord.interviewee || '');

      // ADL
      const newAdlData: Record<string, LifeCheckElement> = {};
      ADL_ITEMS.forEach(item => {
        const value = currentRecord[item.key as keyof LifeCheckRecord] as LifeCheckElement | undefined;
        newAdlData[item.key] = value || defaultElement();
      });
      setAdlData(newAdlData);
      setAdlDetail(currentRecord.adl_detail || '');

      // IADL
      const newIadlData: Record<string, LifeCheckElement> = {};
      IADL_ITEMS.forEach(item => {
        const value = currentRecord[item.key as keyof LifeCheckRecord] as LifeCheckElement | undefined;
        newIadlData[item.key] = value || defaultElement();
      });
      setIadlData(newIadlData);
      setIadlDetail(currentRecord.iadl_detail || '');

      // Behavior
      const newBehaviorData: Record<string, LifeCheckElement> = {};
      BEHAVIOR_ITEMS.forEach(item => {
        const value = currentRecord[item.key as keyof LifeCheckRecord] as LifeCheckElement | undefined;
        newBehaviorData[item.key] = value || defaultElement();
      });
      setBehaviorData(newBehaviorData);
      setBehaviorDetail(currentRecord.behavior_detail || '');
    }
  }, [currentRecord, isEdit]);

  useEffect(() => {
    if (created || updated) {
      onResetFlags();
      history.push(`/institutions/${institutionId}/users/${userId}/records/lifecheck`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated]);

  // datetime-local用のフォーマット変換
  const formatDatetimeForInput = (datetime?: string): string => {
    if (!datetime) return '';
    // "2021-01-01 12:30:00" -> "2021-01-01T12:30"
    return datetime.replace(' ', 'T').substring(0, 16);
  };

  // API送信用のフォーマット変換
  const formatDatetimeForApi = (datetime: string): string => {
    if (!datetime) return '';
    // "2021-01-01T12:30" -> "2021-01-01 12:30:00"
    return datetime.replace('T', ' ') + ':00';
  };

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/lifecheck`);
  };

  // レベル選択のトグル
  const handleLevelToggle = useCallback((category: 'adl' | 'iadl' | 'behavior', key: string, levelIndex: number) => {
    const setData = category === 'adl' ? setAdlData : category === 'iadl' ? setIadlData : setBehaviorData;

    setData(prev => {
      const current = prev[key];
      const levels = [...current.level];
      const idx = levels.indexOf(levelIndex);

      if (idx >= 0) {
        // 既に選択されている場合は削除（ただし最低1つは残す）
        if (levels.length > 1) {
          levels.splice(idx, 1);
        }
      } else {
        // 選択されていない場合は追加
        levels.push(levelIndex);
        levels.sort((a, b) => a - b);
      }

      return {
        ...prev,
        [key]: { ...current, level: levels },
      };
    });
  }, []);

  // 問題ありチェックボックスの変更
  const handleProblemChange = useCallback((category: 'adl' | 'iadl' | 'behavior', key: string, checked: boolean) => {
    const setData = category === 'adl' ? setAdlData : category === 'iadl' ? setIadlData : setBehaviorData;

    setData(prev => ({
      ...prev,
      [key]: { ...prev[key], is_problem: checked },
    }));
  }, []);

  // 環境・備考の変更
  const handleEnvChange = useCallback((category: 'adl' | 'iadl', key: string, value: string) => {
    const setData = category === 'adl' ? setAdlData : setIadlData;

    setData(prev => ({
      ...prev,
      [key]: { ...prev[key], env: value },
    }));
  }, []);

  // 訪問時間のバリデーション（両方入力するか両方空にする必要がある）
  const hasVisitedTimeStart = visitedTimeStart.trim() !== '';
  const hasVisitedTimeEnd = visitedTimeEnd.trim() !== '';
  const isVisitedTimeValid = (hasVisitedTimeStart && hasVisitedTimeEnd) || (!hasVisitedTimeStart && !hasVisitedTimeEnd);

  // 送信可能かチェック
  const canSubmit = () => {
    if (executing) return false;
    // 訪問時間のバリデーション
    if (!isVisitedTimeValid) return false;
    // 全てのADL/IADL/Behavior項目が少なくとも1つのレベルを選択しているか確認
    const allAdlValid = ADL_ITEMS.every(item => adlData[item.key]?.level?.length > 0);
    const allIadlValid = IADL_ITEMS.every(item => iadlData[item.key]?.level?.length > 0);
    const allBehaviorValid = BEHAVIOR_ITEMS.every(item => behaviorData[item.key]?.level?.length > 0);
    return allAdlValid && allIadlValid && allBehaviorValid;
  };

  const handleSubmit = () => {
    // visited_time_start と visited_time_end は両方セットするか両方省略する必要がある
    const shouldIncludeVisitedTime = hasVisitedTimeStart && hasVisitedTimeEnd;

    const data: Partial<LifeCheckRecord> = {
      // 訪問情報
      staff: staff || undefined,
      profession: profession || undefined,
      visited_time_start: shouldIncludeVisitedTime ? formatDatetimeForApi(visitedTimeStart) : undefined,
      visited_time_end: shouldIncludeVisitedTime ? formatDatetimeForApi(visitedTimeEnd) : undefined,
      interviewee: interviewee || undefined,
      // ADL
      ...Object.fromEntries(
        ADL_ITEMS.map(item => [
          item.key,
          {
            level: adlData[item.key].level,
            is_problem: adlData[item.key].is_problem,
            env: adlData[item.key].env || undefined,
          },
        ]),
      ),
      adl_detail: adlDetail || undefined,
      // IADL
      ...Object.fromEntries(
        IADL_ITEMS.map(item => [
          item.key,
          {
            level: iadlData[item.key].level,
            is_problem: iadlData[item.key].is_problem,
            env: iadlData[item.key].env || undefined,
          },
        ]),
      ),
      iadl_detail: iadlDetail || undefined,
      // Behavior
      ...Object.fromEntries(
        BEHAVIOR_ITEMS.map(item => [
          item.key,
          {
            level: behaviorData[item.key].level,
            is_problem: behaviorData[item.key].is_problem,
          },
        ]),
      ),
      behavior_detail: behaviorDetail || undefined,
    };

    if (isEdit && currentRecord) {
      onUpdate(uniqueId, currentRecord.sequence, data);
    } else {
      onCreate(uniqueId, data);
    }
  };

  // 項目入力コンポーネント
  const renderItem = (
    category: 'adl' | 'iadl' | 'behavior',
    item: { key: string; label: string; levels: string[]; hasEnv: boolean },
    data: Record<string, LifeCheckElement>,
  ) => {
    const element = data[item.key];
    return (
      <div key={item.key} className={classes.itemContainer}>
        <Typography className={classes.itemLabel}>{item.label}</Typography>
        <div className={classes.levelChips}>
          {item.levels.map((level, index) => (
            <Chip
              key={index}
              label={level}
              onClick={() => handleLevelToggle(category, item.key, index)}
              className={`${classes.levelChip} ${element.level.includes(index) ? classes.levelChipSelected : ''}`}
              color={element.level.includes(index) ? 'primary' : 'default'}
            />
          ))}
        </div>
        <FormControlLabel
          control={
            <Checkbox
              checked={element.is_problem}
              onChange={e => handleProblemChange(category, item.key, e.target.checked)}
              color="secondary"
            />
          }
          label="問題あり"
          className={`${classes.problemCheckbox} ${element.is_problem ? classes.problemChecked : ''}`}
        />
        {item.hasEnv && (
          <TextField
            label="環境・備考"
            value={element.env || ''}
            onChange={e => handleEnvChange(category as 'adl' | 'iadl', item.key, e.target.value)}
            fullWidth
            size="small"
            className={classes.envField}
          />
        )}
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; 生活機能チェック &gt; {isEdit ? '編集' : '新規作成'}
      </Typography>

      <div className={classes.header}>
        <IconButton className={classes.backButton} onClick={handleBack}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{isEdit ? '生活機能チェック編集' : '生活機能チェック作成'}</Typography>
      </div>

      <Paper className={classes.form}>
        {/* 訪問情報 */}
        <Accordion
          expanded={expandedSections.visit}
          onChange={() => setExpandedSections(prev => ({ ...prev, visit: !prev.visit }))}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography variant="h6">訪問情報</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="担当者" value={staff} onChange={e => setStaff(e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="職種" value={profession} onChange={e => setProfession(e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="訪問開始時間"
                  type="datetime-local"
                  value={visitedTimeStart}
                  onChange={e => setVisitedTimeStart(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!isVisitedTimeValid && !hasVisitedTimeStart}
                  helperText={!isVisitedTimeValid && !hasVisitedTimeStart ? '終了時間と一緒に入力してください' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="訪問終了時間"
                  type="datetime-local"
                  value={visitedTimeEnd}
                  onChange={e => setVisitedTimeEnd(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!isVisitedTimeValid && !hasVisitedTimeEnd}
                  helperText={!isVisitedTimeValid && !hasVisitedTimeEnd ? '開始時間と一緒に入力してください' : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="聞き取り相手"
                  value={interviewee}
                  onChange={e => setInterviewee(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ADL */}
        <Accordion
          expanded={expandedSections.adl}
          onChange={() => setExpandedSections(prev => ({ ...prev, adl: !prev.adl }))}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography variant="h6">ADL（日常生活動作）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ width: '100%' }}>
              <Grid container spacing={2}>
                {ADL_ITEMS.map(item => (
                  <Grid item xs={12} sm={6} key={item.key}>
                    {renderItem('adl', item, adlData)}
                  </Grid>
                ))}
              </Grid>
              <TextField
                label="ADL特記事項"
                value={adlDetail}
                onChange={e => setAdlDetail(e.target.value)}
                fullWidth
                multiline
                rows={3}
                className={classes.detailField}
              />
            </div>
          </AccordionDetails>
        </Accordion>

        {/* IADL */}
        <Accordion
          expanded={expandedSections.iadl}
          onChange={() => setExpandedSections(prev => ({ ...prev, iadl: !prev.iadl }))}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography variant="h6">IADL（手段的日常生活動作）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ width: '100%' }}>
              <Grid container spacing={2}>
                {IADL_ITEMS.map(item => (
                  <Grid item xs={12} sm={4} key={item.key}>
                    {renderItem('iadl', item, iadlData)}
                  </Grid>
                ))}
              </Grid>
              <TextField
                label="IADL特記事項"
                value={iadlDetail}
                onChange={e => setIadlDetail(e.target.value)}
                fullWidth
                multiline
                rows={3}
                className={classes.detailField}
              />
            </div>
          </AccordionDetails>
        </Accordion>

        {/* 居宅内動作 */}
        <Accordion
          expanded={expandedSections.behavior}
          onChange={() => setExpandedSections(prev => ({ ...prev, behavior: !prev.behavior }))}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography variant="h6">居宅内動作</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ width: '100%' }}>
              <Grid container spacing={2}>
                {BEHAVIOR_ITEMS.map(item => (
                  <Grid item xs={12} sm={4} md={2} key={item.key}>
                    {renderItem('behavior', item, behaviorData)}
                  </Grid>
                ))}
              </Grid>
              <TextField
                label="動作特記事項"
                value={behaviorDetail}
                onChange={e => setBehaviorDetail(e.target.value)}
                fullWidth
                multiline
                rows={3}
                className={classes.detailField}
              />
            </div>
          </AccordionDetails>
        </Accordion>

        <Divider style={{ margin: '24px 0' }} />

        {!isVisitedTimeValid && (
          <Typography variant="body2" className={classes.validationError}>
            ※ 訪問開始時間と訪問終了時間は両方入力するか、両方空にしてください
          </Typography>
        )}

        {!canSubmit() && isVisitedTimeValid && (
          <Typography variant="body2" className={classes.validationError}>
            ※ 全てのADL・IADL・居宅内動作項目で少なくとも1つのレベルを選択してください
          </Typography>
        )}

        <div className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleBack} disabled={executing}>
            キャンセル
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!canSubmit()}>
            {executing ? <CircularProgress size={24} /> : isEdit ? '更新' : '作成'}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default LifeCheckRecordForm;
